import { Eye, EyeOff, KeyRound, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  LICENSE_PRODUCTS,
  type BusinessDetailsView,
  type LicenseOperationResult,
  type LicenseProduct,
  type LicenseRevealResult,
  type LiveLicenseRow,
} from '@quds/shared';
import { useCan } from '@/auth/AuthContext';
import {
  Badge,
  Button,
  CopyButton,
  DescriptionList,
  EmptyState,
  FormField,
  Input,
  Modal,
  Select,
  Table,
  TableContainer,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { deleteBusinessLicense, issueBusinessLicense, renewBusinessLicense, revealBusinessLicense } from '@/data';
import { ApiError, errorMessage } from '@/lib/api';
import { formatDate, formatDateTime } from '@/lib/format';

type NearMatch = Array<{ deviceId: string; masked?: string; distance?: number }>;

type Flow =
  | { kind: 'issue' }
  | { kind: 'issued'; result: LicenseOperationResult }
  | { kind: 'reveal'; license: LiveLicenseRow }
  | { kind: 'renew'; license: LiveLicenseRow }
  | { kind: 'renewed'; result: LicenseOperationResult; previousExpiry: string }
  | { kind: 'delete'; license: LiveLicenseRow };

const newKey = () => crypto.randomUUID();

function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function oneYearFrom(base: string | null | undefined): string {
  const start = base ? new Date(base) : new Date();
  const today = new Date();
  const d = Number.isNaN(start.getTime()) || start < today ? today : start;
  const out = new Date(d);
  out.setFullYear(out.getFullYear() + 1);
  return isoDay(out);
}

function productForCategory(categoryKey: string | undefined): LicenseProduct {
  return categoryKey === 'SUPERMARKET' ? 'Quds Supermarket POS' : 'Quds Pharmacy POS';
}

function ErrorBanner({ children }: { children: string }) {
  return (
    <p role="alert" className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
      {children}
    </p>
  );
}

function CodeBox({ id, label, value }: { id: string; label: string; value: string }) {
  return (
    <FormField label={label} htmlFor={id}>
      <textarea
        id={id}
        readOnly
        rows={4}
        value={value}
        onFocus={(e) => e.currentTarget.select()}
        className="w-full resize-none break-all rounded-md border border-line bg-surface-muted px-3 py-2 font-mono text-xs text-ink focus-visible:shadow-focus focus-visible:outline-none"
      />
    </FormField>
  );
}

/**
 * Shared Device License management surface (Business workspace License tab and Manage License modal).
 * Activation codes live only in transient component state and are cleared on Hide / Done / unmount.
 */
export function DeviceLicenseManager({
  businessId,
  businessName,
  categoryKey,
  details,
  onRefresh,
}: {
  businessId: string;
  businessName: string;
  categoryKey?: string;
  details: BusinessDetailsView;
  onRefresh: () => void | Promise<void>;
}) {
  const canManage = useCan()('license.manage');
  const productionAvailable = details.overview.productionAvailable === true;
  const defaultProduct = productForCategory(categoryKey ?? details.overview.category);
  const actionsDisabled = !productionAvailable;
  const disabledTitle = actionsDisabled ? 'Production unavailable' : undefined;

  const licenses = useMemo(
    () =>
      [...details.licensing.licenses].sort(
        (a, b) => (Date.parse(b.generatedAt) || 0) - (Date.parse(a.generatedAt) || 0),
      ),
    [details.licensing.licenses],
  );
  const knownDeviceId = details.licensing.fingerprints[0]?.deviceId ?? '';

  const [flow, setFlow] = useState<Flow | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const keyRef = useRef(newKey());
  const revealSeq = useRef(0);
  const resetKey = () => {
    keyRef.current = newKey();
  };

  // Issue form
  const [deviceId, setDeviceId] = useState('');
  const [customerName, setCustomerName] = useState(businessName);
  const [product, setProduct] = useState<LicenseProduct>(defaultProduct);
  const [expiryDate, setExpiryDate] = useState(oneYearFrom(null));
  const [notes, setNotes] = useState('');
  const [nearMatch, setNearMatch] = useState<NearMatch | null>(null);
  const [acknowledgeNearMatch, setAcknowledgeNearMatch] = useState(false);

  // Reveal state (plaintext) — cleared on Hide
  const [revealed, setRevealed] = useState<LicenseRevealResult | null>(null);
  const [revealLoading, setRevealLoading] = useState(false);

  // Renew form
  const [renewExpiry, setRenewExpiry] = useState(oneYearFrom(null));

  useEffect(
    () => () => {
      setRevealed(null);
    },
    [],
  );

  const closeFlow = () => {
    revealSeq.current += 1; // invalidate any in-flight reveal so late responses are dropped
    setFlow(null);
    setError(null);
    setRevealed(null);
    setRevealLoading(false);
    setNearMatch(null);
    setAcknowledgeNearMatch(false);
  };

  /** Closing a mutation-success dialog drops the plaintext code and refreshes the list. */
  const doneAndRefresh = () => {
    closeFlow();
    void onRefresh();
  };

  const openIssue = () => {
    resetKey();
    setError(null);
    setNearMatch(null);
    setAcknowledgeNearMatch(false);
    setDeviceId(knownDeviceId);
    setCustomerName(businessName);
    setProduct(defaultProduct);
    setExpiryDate(oneYearFrom(null));
    setNotes('');
    setFlow({ kind: 'issue' });
  };

  const submitIssue = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await issueBusinessLicense(businessId, {
        product,
        deviceId: deviceId.trim(),
        customerName: customerName.trim(),
        expiryDate,
        notes: notes.trim() || undefined,
        acknowledgeNearMatch,
        idempotencyKey: keyRef.current,
      });
      setFlow({ kind: 'issued', result });
    } catch (e) {
      if (e instanceof ApiError && e.status === 422) {
        const d = e.details as { candidates?: NearMatch } | undefined;
        if (e.code === 'LICENSE_DEVICE_NEAR_MATCH' || d?.candidates) {
          setNearMatch(d?.candidates ?? []);
          setError('A similar Device ID already exists. Confirm the match below before issuing.');
          return;
        }
      }
      setError(errorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const loadReveal = async (license: LiveLicenseRow) => {
    const seq = ++revealSeq.current;
    setRevealLoading(true);
    setError(null);
    try {
      const res = await revealBusinessLicense(businessId, license.id, { idempotencyKey: keyRef.current });
      if (seq !== revealSeq.current) return;
      // Guard: only show the code for the license that was requested.
      if (res.licenseId !== license.id) throw new Error('Unexpected license returned — please retry.');
      setRevealed(res);
    } catch (e) {
      if (seq !== revealSeq.current) return;
      setError(errorMessage(e, 'License could not be revealed. Please try again.'));
    } finally {
      if (seq === revealSeq.current) setRevealLoading(false);
    }
  };

  const openReveal = (license: LiveLicenseRow) => {
    resetKey();
    setRevealed(null);
    setError(null);
    setFlow({ kind: 'reveal', license });
    void loadReveal(license);
  };

  const openRenew = (license: LiveLicenseRow) => {
    resetKey();
    setError(null);
    setRenewExpiry(oneYearFrom(license.expiryDate));
    setFlow({ kind: 'renew', license });
  };

  const openDelete = (license: LiveLicenseRow) => {
    resetKey();
    setError(null);
    setFlow({ kind: 'delete', license });
  };

  const submitDelete = async (license: LiveLicenseRow) => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await deleteBusinessLicense(businessId, license.id, { idempotencyKey: keyRef.current });
      doneAndRefresh();
    } catch (e) {
      setError(errorMessage(e, 'License could not be deleted. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitRenew = async (license: LiveLicenseRow) => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await renewBusinessLicense(businessId, license.id, {
        expiryDate: renewExpiry,
        idempotencyKey: keyRef.current,
      });
      setFlow({ kind: 'renewed', result, previousExpiry: license.expiryDate });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const issueButton = canManage ? (
    <Button
      variant="primary"
      size="sm"
      leftIcon={<Plus size={14} aria-hidden="true" />}
      onClick={openIssue}
      disabled={actionsDisabled}
      title={disabledTitle}
    >
      Issue License
    </Button>
  ) : null;

  const issueValid = Boolean(deviceId.trim() && customerName.trim() && expiryDate && product);

  return (
    <section aria-label="Device Licenses" className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
            <KeyRound size={16} className="text-ink-muted" aria-hidden="true" />
            Issued license records
          </h3>
          <p className="mt-0.5 text-xs text-ink-secondary">
            Production-generated licenses (issue, reveal activation code, manual renew, delete). {businessName}
          </p>
        </div>
        {licenses.length > 0 && issueButton}
      </div>

      {actionsDisabled && (
        <Badge tone="warning">Production data temporarily unavailable — Issue, Reveal, Renew and Delete are disabled</Badge>
      )}

      {licenses.length === 0 ? (
        <div className="rounded-md border border-line">
          <EmptyState
            compact
            title="No Device Licenses"
            description="Issue a License for a POS computer using its Device ID."
            action={issueButton}
          />
        </div>
      ) : (
        <TableContainer label="Device Licenses">
          <Table>
            <THead>
              <TR>
                <TH>Device ID</TH>
                <TH>Customer</TH>
                <TH>Product</TH>
                <TH>Expiry</TH>
                <TH>Status</TH>
                <TH>Issued</TH>
                <TH align="right">Actions</TH>
              </TR>
            </THead>
            <TBody>
              {licenses.map((lic) => (
                <TR key={lic.id}>
                  <TD>
                    <span className="font-mono text-xs">{lic.deviceId}</span>
                  </TD>
                  <TD>{lic.customerName}</TD>
                  <TD>{lic.product}</TD>
                  <TD>{formatDate(lic.expiryDate)}</TD>
                  <TD>
                    <Badge tone={lic.displayStatus === 'active' ? 'success' : 'warning'} dot>
                      {lic.displayStatus === 'active' ? 'Active' : 'Expired'}
                    </Badge>
                  </TD>
                  <TD>{formatDateTime(lic.generatedAt)}</TD>
                  <TD align="right">
                    {canManage ? (
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          leftIcon={<Eye size={14} aria-hidden="true" />}
                          onClick={() => openReveal(lic)}
                          disabled={actionsDisabled}
                          title={disabledTitle}
                          aria-label={`Reveal license for ${lic.deviceId}`}
                        >
                          Reveal
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          leftIcon={<RefreshCw size={14} aria-hidden="true" />}
                          onClick={() => openRenew(lic)}
                          disabled={actionsDisabled}
                          title={disabledTitle}
                          aria-label={`Renew license for ${lic.deviceId}`}
                        >
                          Renew
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          leftIcon={<Trash2 size={14} aria-hidden="true" />}
                          onClick={() => openDelete(lic)}
                          disabled={actionsDisabled}
                          title={disabledTitle}
                          aria-label={`Delete license for ${lic.deviceId}`}
                        >
                          Delete
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-ink-muted">—</span>
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </TableContainer>
      )}

      <p className="text-xs text-ink-muted">New computer? Issue a new License using the new Device ID.</p>
      {!canManage && (
        <p className="text-[13px] text-ink-secondary">View only: your role can see this information but cannot manage it.</p>
      )}

      {/* Issue form */}
      {flow?.kind === 'issue' && (
        <Modal
          open
          onClose={closeFlow}
          dismissible={!submitting}
          title="Issue License"
          description={businessName}
          icon={<KeyRound size={18} aria-hidden="true" />}
          size="md"
          footer={
            <>
              <Button variant="secondary" onClick={closeFlow} disabled={submitting} className="mr-auto">
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => void submitIssue()}
                loading={submitting}
                disabled={submitting || !issueValid || (Boolean(nearMatch?.length) && !acknowledgeNearMatch)}
              >
                {submitting ? 'Issuing…' : 'Issue'}
              </Button>
            </>
          }
        >
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (issueValid) void submitIssue();
            }}
          >
            {error && <ErrorBanner>{error}</ErrorBanner>}
            <FormField label="Device ID" htmlFor="dl-device" required hint="Exact QUDS-XXXX-XXXX-XXXX-XXXX from the POS computer. Not autocorrected.">
              <Input
                id="dl-device"
                value={deviceId}
                onChange={(e) => {
                  resetKey();
                  setNearMatch(null);
                  setAcknowledgeNearMatch(false);
                  setDeviceId(e.target.value.toUpperCase());
                }}
                className="font-mono"
                autoComplete="off"
                spellCheck={false}
                disabled={submitting}
              />
            </FormField>
            <FormField label="Customer Name" htmlFor="dl-customer" required>
              <Input
                id="dl-customer"
                value={customerName}
                onChange={(e) => {
                  resetKey();
                  setCustomerName(e.target.value);
                }}
                disabled={submitting}
              />
            </FormField>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label="Product" htmlFor="dl-product" required>
                <Select
                  id="dl-product"
                  value={product}
                  onChange={(e) => {
                    resetKey();
                    setProduct(e.target.value as LicenseProduct);
                  }}
                  options={LICENSE_PRODUCTS.map((p) => ({ value: p, label: p }))}
                  disabled={submitting}
                />
              </FormField>
              <FormField label="Expiry" htmlFor="dl-expiry" required>
                <Input
                  id="dl-expiry"
                  type="date"
                  value={expiryDate}
                  onChange={(e) => {
                    resetKey();
                    setExpiryDate(e.target.value);
                  }}
                  disabled={submitting}
                />
              </FormField>
            </div>
            <FormField label="Notes (optional)" htmlFor="dl-notes">
              <Input
                id="dl-notes"
                value={notes}
                onChange={(e) => {
                  resetKey();
                  setNotes(e.target.value);
                }}
                disabled={submitting}
              />
            </FormField>
            {nearMatch && nearMatch.length > 0 && (
              <div className="rounded-md border border-warning/30 bg-warning-soft px-3 py-3 text-sm">
                <p className="font-medium text-warning">Similar Device ID already exists</p>
                <ul className="mt-2 space-y-1 font-mono text-xs">
                  {nearMatch.map((c) => (
                    <li key={c.deviceId}>{c.deviceId}</li>
                  ))}
                </ul>
                <label className="mt-3 flex items-center gap-2 text-[13px]">
                  <input
                    type="checkbox"
                    checked={acknowledgeNearMatch}
                    onChange={(e) => {
                      resetKey();
                      setAcknowledgeNearMatch(e.target.checked);
                    }}
                  />
                  I confirm this is intentional and not a typo
                </label>
              </div>
            )}
          </form>
        </Modal>
      )}

      {/* Issue success */}
      {flow?.kind === 'issued' && (
        <Modal
          open
          onClose={doneAndRefresh}
          title="License Issued Successfully"
          description={businessName}
          icon={<KeyRound size={18} aria-hidden="true" />}
          size="md"
          footer={
            <>
              <CopyButton value={flow.result.activationCode} label="Copy License" copiedLabel="License Copied" variant="secondary" size="md" toastTitle="License copied" className="mr-auto" />
              <Button variant="primary" onClick={doneAndRefresh}>
                Done
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <DescriptionList
              items={[
                { label: 'Device', value: <span className="font-mono text-xs">{flow.result.payload.deviceId}</span> },
                { label: 'Expiry', value: formatDate(flow.result.payload.expiryDate) },
              ]}
            />
            <CodeBox id="dl-issued-code" label="Activation code" value={flow.result.activationCode} />
            <p className="text-xs text-ink-muted">Enter this code on the POS computer to activate it. You can reveal it again later from the table.</p>
          </div>
        </Modal>
      )}

      {/* Reveal */}
      {flow?.kind === 'reveal' && (
        <Modal
          open
          onClose={closeFlow}
          title="License Activation Code"
          description={flow.license.deviceId}
          icon={<Eye size={18} aria-hidden="true" />}
          size="md"
          footer={
            <>
              {revealed && (
                <CopyButton value={revealed.activationCode} label="Copy License" copiedLabel="License Copied" variant="secondary" size="md" toastTitle="License copied" className="mr-auto" />
              )}
              <Button variant="primary" leftIcon={<EyeOff size={14} aria-hidden="true" />} onClick={closeFlow}>
                Hide
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            {error && <ErrorBanner>{error}</ErrorBanner>}
            {revealLoading && <p role="status" className="text-sm text-ink-secondary">Revealing license…</p>}
            {!revealLoading && error && (
              <Button variant="secondary" size="sm" onClick={() => void loadReveal(flow.license)}>
                Try again
              </Button>
            )}
            {revealed && (
              <>
                <DescriptionList
                  items={[
                    { label: 'Device', value: <span className="font-mono text-xs">{revealed.deviceId}</span> },
                    { label: 'Customer', value: revealed.customerName },
                    { label: 'Product', value: revealed.product },
                    { label: 'Expiry', value: formatDate(revealed.expiryDate) },
                  ]}
                />
                <CodeBox id="dl-revealed-code" label="Activation code" value={revealed.activationCode} />
              </>
            )}
          </div>
        </Modal>
      )}

      {/* Renew */}
      {flow?.kind === 'renew' && (
        <Modal
          open
          onClose={closeFlow}
          dismissible={!submitting}
          title="Renew License"
          description={flow.license.deviceId}
          icon={<RefreshCw size={18} aria-hidden="true" />}
          size="md"
          footer={
            <>
              <Button variant="secondary" onClick={closeFlow} disabled={submitting} className="mr-auto">
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => void submitRenew(flow.license)}
                loading={submitting}
                disabled={submitting || !renewExpiry}
              >
                {submitting ? 'Renewing…' : 'Renew License'}
              </Button>
            </>
          }
        >
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (renewExpiry) void submitRenew(flow.license);
            }}
          >
            {error && <ErrorBanner>{error}</ErrorBanner>}
            <DescriptionList
              items={[
                { label: 'Device', value: <span className="font-mono text-xs">{flow.license.deviceId}</span> },
                { label: 'Current expiry', value: formatDate(flow.license.expiryDate) },
              ]}
            />
            <FormField label="New expiry" htmlFor="dl-renew-expiry" required>
              <Input
                id="dl-renew-expiry"
                type="date"
                value={renewExpiry}
                onChange={(e) => {
                  resetKey();
                  setRenewExpiry(e.target.value);
                }}
                disabled={submitting}
              />
            </FormField>
            <p className="text-xs text-ink-muted">Renewing generates a new activation code that must be entered on the POS computer.</p>
          </form>
        </Modal>
      )}

      {/* Renew success */}
      {flow?.kind === 'renewed' && (
        <Modal
          open
          onClose={doneAndRefresh}
          title="License Renewed"
          description={businessName}
          icon={<RefreshCw size={18} aria-hidden="true" />}
          size="md"
          footer={
            <>
              <CopyButton value={flow.result.activationCode} label="Copy License" copiedLabel="License Copied" variant="secondary" size="md" toastTitle="License copied" className="mr-auto" />
              <Button variant="primary" onClick={doneAndRefresh}>
                Done
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <p className="rounded-md border border-warning/30 bg-warning-soft px-3 py-2 text-[13px] text-warning">
              A new activation code is required. Enter it on the POS computer to apply the new expiry.
            </p>
            <DescriptionList
              items={[
                { label: 'Device', value: <span className="font-mono text-xs">{flow.result.payload.deviceId}</span> },
                { label: 'Previous expiry', value: formatDate(flow.previousExpiry) },
                { label: 'New expiry', value: formatDate(flow.result.payload.expiryDate) },
              ]}
            />
            <CodeBox id="dl-renewed-code" label="New activation code" value={flow.result.activationCode} />
          </div>
        </Modal>
      )}

      {/* Delete confirmation — administrative record removal, not offline revocation */}
      {flow?.kind === 'delete' && (
        <Modal
          open
          onClose={closeFlow}
          dismissible={!submitting}
          title="Delete Device License?"
          description={flow.license.deviceId}
          icon={<Trash2 size={18} aria-hidden="true" />}
          size="md"
          footer={
            <>
              <Button variant="secondary" onClick={closeFlow} disabled={submitting} className="mr-auto">
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => void submitDelete(flow.license)}
                loading={submitting}
                disabled={submitting}
              >
                {submitting ? 'Deleting…' : 'Delete License'}
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            {error && <ErrorBanner>{error}</ErrorBanner>}
            <p className="text-sm text-ink-secondary">
              This removes the License record from Quds administration.
            </p>
            <p className="rounded-md border border-warning/30 bg-warning-soft px-3 py-2 text-[13px] text-warning">
              If this activation code was already copied or used on a POS computer, deleting the record will NOT
              deactivate that offline License.
            </p>
            <DescriptionList
              items={[
                { label: 'Device ID', value: <span className="font-mono text-xs">{flow.license.deviceId}</span> },
                { label: 'Customer', value: flow.license.customerName },
                { label: 'Expiry', value: formatDate(flow.license.expiryDate) },
              ]}
            />
          </div>
        </Modal>
      )}
    </section>
  );
}
